export enum AccessStatus {
  granted = 'granted',
  subscriptionRequired = 'subscriptionRequired',
  privateAccessRequired = 'privateAccessRequired',
}

export interface SecurityRating {
  agency: string;
  bond_id: number;
  loan_id: number;
  rating: string;
  status: string;
  type: string;
}

export interface SecurityDocument {
  bond_id: number;
  changed: string;
  created: string;
  doc_format: string;
  doc_length: number;
  doc_location: string;
  doc_name: string;
  doc_type: string;
  doc_url: string;
  doc_date: string;
}

export interface Security {
  issuer_tag_id: string | undefined;
  id: number;
  add_on: number;
  bank_meeting_date: string;
  call_structure: string;
  callable: number;
  closing_date: string;
  commitment_date: string;
  company_name: string;
  country_tag: string;
  country_text: string;
  coupon: string;
  coupon_type: string;
  calls: {
    bond_id: number;
    call_date: string;
    call_price: number;
  }[];
  cs_companyid: number;
  currency: string;
  deal_status: string;
  deal_type: string;
  docs: SecurityDocument[];
  euro_equiv: number;
  facilitys: {
    created: string;
    curr: string;
    facility_floor: string;
    facility_interest: string;
    facility_oid: number;
    facility_payment_type: string;
    facility_term: string;
    facility_tranche_type: string;
    loan_id: number;
    margin: string;
    note: string;
    price_tlb: string;
    size: number;
  }[];
  fitch_rating_value: string;
  headline: string;
  issuers: string[];
  isin: string;
  last_px_talk: string;
  launch_date: string;
  leads: {
    bond_id: number;
    company: string;
    lead_id: number;
    lead_role: string;
    loan_id: number;
    role: string;
  }[];
  leverages: {
    leverage_type: string;
    leverage_value: number;
    loan_id: number;
  }[];
  loan_bond_id: number;
  market_status: string;
  maturity_date: string;
  moderation: string;
  moodys_rating_value: string;
  notes: string;
  pricing_date: string;
  proceeds: number;
  publish_status: string;
  region: string[];
  report_id: number;
  ratings: SecurityRating[];
  sector_tag: string[];
  sector_text: string[];
  security_type: string;
  settlement_date: string;
  size: number;
  sp_rating_value: string;
  special_features: {
    bond_id: number;
    loan_id: number;
    special_feature: string;
    special_feature_id: number;
  }[];
  sponsors: {
    bond_id: number;
    loan_id: number;
    sponsor: string;
    sponsor_id: number;
    lfi_company_types: string[];
  }[];
  topic_si: string;
  uop: string;
  uop_raw: string;
  usd_equiv: number;
  ytm: string;
  price: number;
  price_type: string;

  // metadata
  can_access: boolean;
  access_status: AccessStatus;
  issuer_implies: string[];
  issuer_regions: string[];
  issuer_credit_grades: string[];
  issuer_sectors: string[];
}

export interface SecuritiesResults {
  totalResults: number;
  results: Security[];
}

export interface CovenantComparison {
  id: string;
  cs_universal_id: string;
  entity_name: string;
  issuer: string;
  report_id: number;
  issue_name: string;
  interest_rate: string;
  interest_rate_type: string;
  issue_date: string;
  maturity_date: string;
  use_of_proceeds: string;
  country_code: string;
  industry: string;
  regions: string[];
  region: string;
  sectors: string[];

  // metadata
  can_access: boolean;
  access_status: AccessStatus;
  issuer_implies: string[];
}

export interface CovenantComparisonResults {
  totalResults: number;
  results: CovenantComparison[];
}

export interface LoanComparison {
  id: string;
  transaction_id: string;
  borrower: string;
  industry: string;
  purpose: string;
  launch_date: string;
  report_id: number;
  phase: string;
  arrangers: string[];
  sponsors: string[];
  size: number;
  cs_universal_id: string;
  entity_name: string;
  issuer: string;
  country_code: string;
  regions: string[];
  region: string;
  sectors: string[];

  // metadata
  can_access: boolean;
  access_status: AccessStatus;
  issuer_implies: string[];
}

export interface LoanComparisonResults {
  totalResults: number;
  results: LoanComparison[];
}
